import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { isMobileSelector } from 'redux/selectors/global';
import { getCheckoutPageData } from 'redux/selectors/checkout';
import { usePmtTranslation } from 'hooks/usePmtTranslation';
import Dropdown from 'components/formElements/Dropdown/Dropdown';
import CachedImg from 'components/widgets/CachedImg/CachedImg';
import { IPaymentMethodType } from 'models/paymentMethodType';
import { isRequired } from 'utilities/form/validations';
import { getPmtFallbackImageSrc } from 'utilities/paymentMethodType';
import { useLoadImage } from 'hooks';
import { Label } from 'styles/global';
import { PMT_FIELD_NAME, GA_ACTIONS } from 'consts';

interface IProps {
  className?: string;
  pmts: IPaymentMethodType[];
  category: string;
  title?: string;
  placeholder?: string;
}

const PmtOption = ({
  optionData,
  fallbackSrc
}: {
  optionData: any;
  fallbackSrc: string;
}) => {
  const [loaded, setLoaded] = useState(false);
  const { label, image } = optionData || {};
  const { isLoaded: isValidSrc } = useLoadImage(image);

  return (
    <PmtOptionContent title={label}>
      <div>{label}</div>
      <PmtIconWrapper loaded={loaded}>
        <PmtImage
          src={image}
          fallbackSrc={isValidSrc ? image : fallbackSrc}
          onLoad={() => setLoaded(true)}
        />
      </PmtIconWrapper>
    </PmtOptionContent>
  );
};

const TypesDropdown = ({ className, pmts, title, category, placeholder }: IProps) => {
  const { t } = useTranslation();
  const isMobile = useSelector(isMobileSelector);
  const checkoutPage = useSelector(getCheckoutPageData);
  const { payment_method_types: paymentMethodTypes } = checkoutPage || {};
  const isOneCategory = paymentMethodTypes?.length === 1;

  const fallbackSrc = getPmtFallbackImageSrc(category);

  const normalizedPmtOptionsTranslated: Array<{
    label: string;
    value: string;
    image: string;
  }> =
    // TODO: remove the ts ignore once hook will be ts too
    // @ts-ignore
    usePmtTranslation({
      data: pmts
    });

  const formatOptionLabelArray = useMemo(() => {
    if (
      !Array.isArray(normalizedPmtOptionsTranslated) ||
      !normalizedPmtOptionsTranslated.length
    ) {
      return [];
    }
    return normalizedPmtOptionsTranslated.map(option => {
      return {
        value: option.value,
        element: (
          <PmtOption optionData={option} fallbackSrc={fallbackSrc} key={option.value} />
        )
      };
    });
  }, [normalizedPmtOptionsTranslated]);

  const initialValue = normalizedPmtOptionsTranslated?.length
    ? normalizedPmtOptionsTranslated[0].value
    : null;

  return (
    <div className={className} data-testid="types-dropdown">
      {title && (
        <StyledTitle data-testid="details_title" isOneCategory={isOneCategory}>
          {t(title)}
        </StyledTitle>
      )}
      <Field
        name={PMT_FIELD_NAME}
        component={StyledTypesDropdown}
        isClearable={false}
        options={normalizedPmtOptionsTranslated}
        validate={isRequired}
        initialValue={initialValue}
        placeholder={placeholder}
        formatOptionLabel={(optionData: any) => {
          const { value: optionValue } = optionData || {};
          const option = formatOptionLabelArray.find(
            option => option?.value === optionValue
          );
          return option?.element || null;
        }}
        isSearchable={!isMobile || (normalizedPmtOptionsTranslated || []).length > 10}
        oneOptionStyleDropdownAsDiv
        gaAction={GA_ACTIONS.SELECTED_PMT}
      />
      <HiddenPreloadImages>
        {formatOptionLabelArray.map(option => option.element)}
      </HiddenPreloadImages>
    </div>
  );
};

export default TypesDropdown;

const PmtOptionContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div:first-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 0.8rem;
  }
`;

const StyledTitle = styled(Label)<{ isOneCategory: boolean }>`
  ${({ theme, isOneCategory }) => {
    const { isMobile, isSdk } = theme.view;
    if (!isMobile && !isSdk && isOneCategory) {
      return css`
        margin-top: 0.4rem;
      `;
    }
  }};

  ${({ theme }) => {
    const { dropdown } = theme.hcp || {};
    if (dropdown) {
      return css`
        color: ${dropdown?.title?.color};
      `;
    }
  }}
`;

const StyledTypesDropdown = styled(Dropdown)`
  margin-top: ${({ theme }) => (theme.view.isMobile ? '2.5rem' : '2rem')};

  .dropdown__single-value ${PmtOptionContent} {
    padding-right: 0.8rem;
    font-size: ${({ theme }) => theme.fonts.size.small};
  }
  .dropdown__option {
    width: 100%;
  }
`;

const PmtIconWrapper = styled.div<{ loaded: boolean }>`
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.3s;
  height: 2rem;
  width: 6rem;

  ${({ loaded }) =>
    loaded &&
    css`
      opacity: 1;
    `};
`;

const PmtImage = styled(CachedImg)`
  object-fit: contain;
  object-position: right;
  height: 100%;
  width: 100%;
`;

const HiddenPreloadImages = styled.div`
  display: none;
`;
